/* eslint-disable no-param-reassign */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import ReactDOMServer from "react-dom/server";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ArrowLine from "./ArrowLine";

const ArrowLabel = ({
  viewBox,
  setLabelDims,
  labelPos,
  arrowLineWidth,
  labelPadding,
  bottomPadding,
  textLabels,
  curve,
  arrowLineHeight,
  tooltipPayload,
  infoCirclePadding,
}) => {
  const [lastTSpanDims, setLastTSpanDims] = useState({
    width: 0,
    height: 0,
  });
  const [infoCircleDims, setInfoCircleDims] = useState({
    width: 0,
    height: 0,
  });
  const [fontSize, setFontSize] = useState(0);
  const computedArrowLineHeight = arrowLineHeight * fontSize;
  const computedInfoCirclePadding = infoCirclePadding * fontSize;
  const { x, y, width: RefAreaWidth } = viewBox;
  const colorGray = "#6b6b6b";
  const rectCenter = RefAreaWidth / 2;
  const elLabel = useRef();
  const elLastTSpan = useRef();
  const elInfoCircle = useRef();
  const labelYPos =
    (computedArrowLineHeight + textLabels.length * fontSize * 1.3) * -1;
  const arrowEndPosY = (computedArrowLineHeight + fontSize * 0.4) * -1;
  let arrowEndPosX = arrowLineWidth;
  let labelXPos;
  let arrowHeadRotate = 0;
  let xStartPos = 0;

  if (!curve) {
    labelPadding *= 2;
    if (labelPos === "right") {
      arrowHeadRotate = 35;
      xStartPos = 5;
    } else if (labelPos === "left") {
      arrowHeadRotate = 330;
      xStartPos = -5;
    }
  }

  if (labelPos === "right") {
    labelXPos = arrowEndPosX + labelPadding;
  } else if (labelPos === "left") {
    labelXPos = (arrowEndPosX + lastTSpanDims.width) * -1;
    // add extra padding if tooltip/infoCircle is included
    if (tooltipPayload) {
      labelXPos -= infoCircleDims.width + computedInfoCirclePadding * 2.3;
    }
    arrowEndPosX *= -1;
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      `Prop labelPos has invalid value ${labelPos}. Should be set as 'left' or 'right'.`
    );
  }

  useEffect(() => {
    const tempLabelDims = elLabel.current.getBoundingClientRect();
    const tempLastTSpanDims = elLastTSpan.current.getBoundingClientRect();
    const tempFontSize = window
      .getComputedStyle(elLastTSpan.current, null)
      .getPropertyValue("font-size");
    const cleanFontSize = parseFloat(tempFontSize.replace("px", ""));
    setFontSize(cleanFontSize);
    const tempInfoCircleDims = elInfoCircle.current.getBoundingClientRect();
    setLabelDims({
      right: tempLabelDims.right,
      left: tempLabelDims.left,
      top: tempLabelDims.top,
      bottom: tempLabelDims.bottom,
    });
    setLastTSpanDims(tempLastTSpanDims);
    setInfoCircleDims(tempInfoCircleDims);
    /* Note: We need to rebuild tooltip because <ReactTooltip> is rendered ahead of this component. 
    That means the tooltip will not register its event listener */
    ReactTooltip.rebuild();
  }, [setLabelDims, tooltipPayload, elLabel, elLastTSpan, elInfoCircle]);

  return (
    <React.Fragment>
      <g transform={`translate(${x + rectCenter},${y - bottomPadding})`}>
        <ArrowLine
          xStartPos={xStartPos}
          yStartPos={-2}
          xEndPos={arrowEndPosX}
          yEndPos={arrowEndPosY}
          curve={curve}
          arrowHeadRotate={arrowHeadRotate}
        />
        <g transform={`translate(${labelXPos},${labelYPos})`} ref={elLabel}>
          <text
            textAnchor="start"
            fill={colorGray}
            className="annotation__label"
          >
            {textLabels.map((label, idx) => {
              const ref =
                textLabels.length === idx + 1 ? elLastTSpan : undefined;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <tspan key={label + idx} x={0} dy="1.2em" ref={ref}>
                  {label}
                </tspan>
              );
            })}
          </text>
          {tooltipPayload && (
            <g ref={elInfoCircle}>
              <FontAwesomeIcon
                data-for="tooltip__info-icon"
                data-tip={ReactDOMServer.renderToStaticMarkup(tooltipPayload)}
                y="1.36em"
                x={lastTSpanDims.width + computedInfoCirclePadding}
                icon={faInfoCircle}
                width="1em"
                height="1em"
                className="annotation__info-circle"
              />
            </g>
          )}
        </g>
      </g>
    </React.Fragment>
  );
};

ArrowLabel.defaultProps = {
  viewBox: null,
  setLabelDims: null,
  labelPos: "right",
  arrowLineWidth: 10,
  arrowLineHeight: 2,
  labelPadding: 10,
  bottomPadding: 10,
  curve: null,
  tooltipPayload: null,
  infoCirclePadding: 0.4,
};

ArrowLabel.propTypes = {
  viewBox: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  setLabelDims: PropTypes.func,
  labelPos: PropTypes.string,
  arrowLineWidth: PropTypes.number,
  labelPadding: PropTypes.number,
  bottomPadding: PropTypes.number,
  textLabels: PropTypes.arrayOf(PropTypes.any).isRequired,
  curve: PropTypes.bool,
  arrowLineHeight: PropTypes.number,
  tooltipPayload: PropTypes.node,
  infoCirclePadding: PropTypes.number,
};

export default ArrowLabel;
